import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { media } from "@literati/public-ui-shared-react";
import {
  BookPreference,
  BookSchemaCamelCase,
  getBookOptions,
  submitBookPreferences,
} from "lib/api/onboarding";
import { ParsedErrorResponse, isAPIError } from "lib/api/base";
import { Subscription } from "lib/api/subscription";
import LoadingAnimation from "@/atoms/LoadingAnimation";
import StepLayout, { StepLayoutProps } from "components/Step/StepLayout";
import { BookShuffleDeck } from "components/bookShuffle/BookShuffleDeck";

const BookShuffleContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  width: 100vw;
  min-height: 567px;
  padding: 2rem 0;

  ${media.lg} {
    padding: 5rem 0;
    min-height: 720px;
  }
`;

const ErrorContainer = styled.div`
  color: red;
  margin: 2rem;
`;

export type StepLayoutPropsBookShuffleOnboarding = Omit<
  StepLayoutProps<object>,
  "asForm" | "children" | "formId"
>;

export interface StepBookShuffleOnboardingProps
  extends StepLayoutPropsBookShuffleOnboarding {
  childAge?: number | null;
  onBookLoadFailure?: (res?: ParsedErrorResponse) => Promise<unknown>;
  onFinish?: () => void;
  subscription?: Partial<Subscription> | null;
  subscriptionId?: number;
}

export const StepBookShuffleOnboarding = ({
  childAge: _childAge,
  onBookLoadFailure,
  onFinish,
  subscription,
  subscriptionId: _subscriptionId,
  stepFooterProps,
  ...stepLayoutProps
}: StepBookShuffleOnboardingProps) => {
  const [books, setBooks] = useState<BookSchemaCamelCase[]>();
  const [bookSwipesComplete, setBookSwipesComplete] = useState(false);
  const [bookError, setBookError] = useState<boolean>(false);

  let childAge = _childAge ?? subscription?.childAge;
  if (typeof childAge === "number" && childAge < 1) {
    childAge = 0.5;
  }
  const subscriptionId = _subscriptionId || subscription?.id;

  useEffect(() => {
    async function fetchBookOptions() {
      const res = await getBookOptions({ childAge, subscriptionId });
      if (isAPIError(res)) {
        onBookLoadFailure?.(res);
        setBookError(true);
        return;
      }
      setBooks(res.results);
    }

    fetchBookOptions();
  }, [childAge, onBookLoadFailure, subscriptionId]);

  const handleSubmitBookPreference = async (bookPreference: BookPreference) => {
    const res = await submitBookPreferences([bookPreference], subscriptionId);
    if (isAPIError(res)) {
      console.error(
        "Error occurred while submitting book shuffle preference",
        res
      );
    }
  };

  return (
    <StepLayout
      asForm={false}
      bgColor="periwinkleTint5"
      disableContentSpacing
      stepFooterProps={{
        ...stepFooterProps,
        nextButtonProps: {
          "aria-label": "Finish",
          children: "Finish",
          onClick: onFinish,
          ...stepFooterProps?.nextButtonProps,
        },
      }}
      suppressFooter={!bookSwipesComplete}
      {...stepLayoutProps}
    >
      {books ? (
        <BookShuffleContainer>
          <BookShuffleDeck
            onSkip={onFinish}
            books={books}
            setBookSwipesComplete={setBookSwipesComplete}
            submitBookPreference={handleSubmitBookPreference}
          />
        </BookShuffleContainer>
      ) : bookError ? (
        <ErrorContainer>Could not load books</ErrorContainer>
      ) : (
        <LoadingAnimation
          css="height: 100dvh"
          className="book-shuffle-loading-animation"
          inline
          message="Loading Books..."
        />
      )}
    </StepLayout>
  );
};

export default StepBookShuffleOnboarding;
